import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
  CircularProgress
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';
import apiClient from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

interface Organization {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
}

interface OrganizationSelectorProps {
  selectedOrgId?: string;
  onOrgChange: (orgId: string | null) => void;
  showAllOption?: boolean;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  selectedOrgId,
  onOrgChange,
  showAllOption = true
}) => {
  const { isSuperAdmin } = useAuth();

  // Получаем список организаций только для супер-админа
  const { data: organizationsData, isLoading } = useQuery(
    ['organizations-for-selector'],
    async () => {
      const response = await apiClient.get('/api/super-admin/organizations');
      return response.data.data;
    },
    {
      enabled: isSuperAdmin,
      retry: 1
    }
  );

  const organizations = organizationsData?.organizations || [];

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <Box sx={{ minWidth: 200, maxWidth: 300 }}>
      <FormControl fullWidth size="small">
        <InputLabel>Организация</InputLabel>
        <Select
          value={selectedOrgId || ''}
          onChange={(e) => onOrgChange(e.target.value || null)}
          label="Организация"
          disabled={isLoading}
          startAdornment={
            isLoading ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : (
              <BusinessIcon sx={{ mr: 1, fontSize: 18 }} />
            )
          }
        >
          {showAllOption && (
            <MenuItem value="">
              <Typography variant="body2" color="primary">
                Все организации
              </Typography>
            </MenuItem>
          )}
          {organizations.map((org: Organization) => (
            <MenuItem key={org.id} value={org.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {org.name}
                </Typography>
                <Chip 
                  label={org.code} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
                {!org.isActive && (
                  <Chip 
                    label="Неактивна" 
                    size="small" 
                    color="error"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: '20px' }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default OrganizationSelector;
